import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Tracker from "@/components/track/Tracker";
import { useEnterShop } from "@/lib/useShop";
import { input } from "@/components/simple/product/Fields";

const btnSecondary = "flex min-h-[48px] items-center justify-center border border-ink bg-page px-4 text-[16px] font-semibold text-ink disabled:opacity-50";

/** `/track` and `/track/:code` (7.9): code or phone, one Find. A logged-in customer lands on My Orders instead. */
export default function TrackOrder() {
    const { code } = useParams();
    const { user, ready } = useAuth();
    const navigate = useNavigate();
    const [q, setQ] = useState(code || "");
    const [orders, setOrders] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const find = async (value) => {
        const term = (value ?? q).trim();
        if (!term) return;
        setBusy(true); setError(""); setOrders(null);
        try {
            const { data } = await api.get("/orders/track", { params: { q: term } });
            setOrders(data.orders);
            if (data.orders.length === 1 && data.orders[0].order_number !== code) navigate(`/track/${data.orders[0].order_number}`, { replace: true });
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "We can't find that order.");
        } finally { setBusy(false); }
    };

    useEffect(() => { if (code) { setQ(code); find(code); } }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
    useEnterShop(orders?.[0]?.shop?.slug || null);

    if (!code && ready && user) return <Navigate to="/account/purchases" replace />;

    return (
        <Layout title="Track your order">
            <section className="mx-auto w-full max-w-4xl px-3 py-4 text-ink sm:px-6" data-testid="track-page">
                <h1 className="text-[28px] font-semibold leading-tight">Track your order</h1>
                <form onSubmit={(e) => { e.preventDefault(); find(); }} className="mt-4 flex flex-col gap-3 sm:flex-row" data-testid="track-form">
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order code or phone number" className={input}
                        autoComplete="off" aria-label="Order code or phone number" data-testid="track-input" />
                    <button type="submit" disabled={busy || !q.trim()} className={`${orders?.length ? btnSecondary : "flex min-h-[48px] items-center justify-center border border-ink bg-accent-brand px-4 text-[16px] font-semibold text-on-accent disabled:opacity-50"} sm:w-40`} data-testid="track-button">{busy ? "Looking…" : "Find"}</button>
                </form>
                {error && (
                    <div className="mt-4" data-testid="track-not-found">
                        <p className="text-[16px]" style={{ color: "var(--error)" }}>{error}</p>
                        <p className="mt-2 flex flex-wrap gap-4 text-[16px]">
                            <Link to="/" className="flex min-h-[44px] items-center underline" data-testid="track-home">Home</Link>
                            <Link to="/login" className="flex min-h-[44px] items-center underline" data-testid="track-login">Log in to see your orders</Link>
                        </p>
                    </div>
                )}
                {orders && orders.length > 1 && <p className="mt-4 text-[16px]" data-testid="track-count">{orders.length} orders on this number — newest first.</p>}
                <div className="mt-4 flex flex-col gap-4">
                    {(orders || []).map((o) => <Tracker key={o.id} order={o} />)}
                </div>
            </section>
        </Layout>
    );
}
